// ============================================================================
// NIGHT AUDIT TYPES (PMS Phase 2 slice 6a, decision P2-18)
// ============================================================================
// Mirrors crs.Core.DTOs.NightAuditModule. Dates are ISO strings from the API.

/** Actions of the Night Audit screen (module `pms-night-audit`). */
export const NIGHT_AUDIT_ACTIONS = {
  view: 'view',
  /** Run the audit and close the business date. */
  submit: 'submit',
} as const;

export const NIGHT_AUDIT_EXCEPTION_LABELS: Record<string, string> = {
  ARRIVAL_NOT_CHECKED_IN: 'Arrival not checked in',
  DUE_OUT_IN_HOUSE: 'Due out, still in house',
  NO_AGREED_RATE: 'No agreed rate',
  ROOM_CHARGING_OFF: 'Room charging off',
  OVER_CREDIT_LIMIT: 'Over credit limit',
  GRC_INCOMPLETE: 'GRC incomplete',
  NO_FOLIO: 'No folio',
  POSTING_FAILED: 'Posting failed',
};

export interface NightAuditException {
  kind: string;
  bookingId?: string | null;
  bookingNumber?: string | null;
  roomNumber?: string | null;
  guestName?: string | null;
  folioId?: string | null;
  message: string;
}

export interface NightAuditRun {
  id: string;
  propertyId: string;
  auditDate: string;
  auditStatusCode: string;
  auditStatusName?: string | null;
  startedAt?: string | null;
  startedByName?: string | null;
  completedAt?: string | null;
  completedByName?: string | null;
  failedReason?: string | null;
  totalRooms: number;
  occupiedRooms: number;
  availableRooms: number;
  oooRooms: number;
  complimentaryRooms: number;
  occupancyPercent: number;
  roomRevenue: number;
  mealRevenue: number;
  addonRevenue: number;
  otherRevenue: number;
  totalRevenue: number;
  adr: number;
  revpar: number;
  arrivalsCount: number;
  departuresCount: number;
  noShowCount: number;
  linesPosted: number;
  backPostedNights: number;
  taxPosted: number;
  exceptionsAcknowledged: boolean;
  exceptions: NightAuditException[];
}

export interface NightAuditStatus {
  propertyId: string;
  businessDate: string;
  closedThrough?: string | null;
  goLiveDate?: string | null;
  /** No night or add-on before it is posted; required before the first run, fixed after it. */
  startDate?: string | null;
  startDateSetAt?: string | null;
  startDateSetByName?: string | null;
  startDateLocked: boolean;
  nextAuditDate: string;
  openDate: string;
  canRun: boolean;
  whyNot?: string | null;
  lastRun?: NightAuditRun | null;
}

export interface NightAuditPosting {
  folioId: string;
  folioNumber?: string | null;
  bookingId: string;
  bookingNumber: string;
  roomNumber?: string | null;
  guestName?: string | null;
  chargeType: string;
  serviceDate: string;
  chargeDate: string;
  description: string;
  amount: number;
  isTaxInclusive: boolean;
  isBackPosted: boolean;
}

/** A posted night the run corrects first: re-priced, or not stayed (newAmount 0) — PMS slice 6b. */
export interface NightRevision {
  chargeId: string;
  bookingId: string;
  bookingNumber: string;
  roomNumber?: string | null;
  chargeType: string;
  serviceDate: string;
  postedAmount: number;
  newAmount: number;
  why: string;
}

export interface NightAuditPreview {
  auditDate: string;
  isFirstAudit: boolean;
  status: NightAuditStatus;
  blockers: string[];
  canRun: boolean;
  taxCalculated: boolean;
  occupiedRooms: number;
  complimentaryRooms: number;
  roomNights: number;
  backPostedNights: number;
  roomAmount: number;
  mealAmount: number;
  addonAmount: number;
  postings: NightAuditPosting[];
  exceptions: NightAuditException[];
  revisions: NightRevision[];
  /** Shown only — never blocking, never acknowledged (e.g. Front Office not closed, slice 7d). */
  warnings?: string[];
}

export interface NightAuditRunRequest {
  propertyId: string;
  auditDate: string;
  acknowledgeExceptions: boolean;
}

export interface NightAuditRunResult {
  run: NightAuditRun;
  closed: boolean;
  failures: NightAuditException[];
}
